/**
 * Normalized flat types — used in the DB, API responses, and local app storage.
 * All SIKB codes are plain strings. No { waarde } envelopes here.
 */

import type { LayerGrainSize } from '../grainSize.js';

export type BzbItem = {
  type?: string | null;       // human label e.g. "Hout"
  code?: string | null;       // 2-letter SIKB code e.g. "HO"
  gradation?: string | null;  // numeric code e.g. "1" (voor 'wortels': dichtheid-term)
  origin?: string | null;     // code e.g. "AN"
  size?: string | null;       // NEN 6693 — grootte (diameter) bij het BZB-type 'wortels' (Tabel 28)
  asphaltType?: string | null; // SIKB — type asfalt bij het BZB-type 'asfalt' (specificatielijst)
  ballastFraction?: string | null; // fractie (korrelmaat) bij het BZB-type 'ballast'
};

export type Layer = {
  id?: string;
  boringId?: string;
  upperDepth: number;           // cm
  lowerDepth: number;           // cm
  soilType?: string | null;     // SIKB code e.g. "Z"
  mediaan?: string | null;      // SIKB code e.g. "3"
  kleurMain?: string | null;    // SIKB code e.g. "GR"
  kleurShade?: string | null;   // SIKB code e.g. "LI"
  kleurSub?: string | null;     // SIKB code e.g. "BR"
  toevoeging1?: string | null;
  gradatie1?: string | null;
  toevoeging2?: string | null;
  gradatie2?: string | null;
  toevoeging3?: string | null;
  gradatie3?: string | null;
  oilWaterReaction?: string | null;  // LaOWTypeCode — olie/water-reactie code
  oilWaterObservation?: string | null; // NEN 6693 — andere waarneming olie-watertest (Tabel 31)
  geurType?: string | null;          // TI LaOdourTypeCode — geur-type
  geurIntensity?: string | null;     // TI LaOdourIntensityCode — geur-gradatie
  drillingSystem?: string | null;    // BOORSYSTEEM-code — boormethode per laag (→ TI LaDrillingSystemTypeCode)
  mineraalarm?: boolean | null;      // NEN 6693 — mineraalarm bij organische grondsoort (veen/humus/detritus/gyttja)
  consistentie?: string | null;      // NEN 6693 — consistentie/rijping bij slib (Tabel 22/25)
  organic?: string | null;           // NEN-EN-ISO 14688 — organisch: 'zwak' | 'sterk'
  grainAngularity?: string | null;   // NEN-EN-ISO 14688 — korrelhoekigheid (deel 1, tabel 4)
  grainShape?: string | null;        // NEN-EN-ISO 14688 — korrelvorm: langwerpig/plat/bolvormig
  peatTexture?: string | null;       // NEN-EN-ISO 14688 — veentextuur bij organische grond (TerraIndex)
  bzb?: BzbItem[];
  opmerking?: string | null;
  /**
   * De korrelverdeling: lutum, leem, grind, silt, organische stof en M50.
   *
   * Staat hij er, dan zijn soilType, de bijmengingen, de gradaties, de mediaan en mineraalarm
   * hiervan AFGELEID en niet zelf gekozen -- de percentages zijn leidend. Zie
   * grainSizeLayerFields in grainSize.ts; de backend rekent ze bij elk opslaan opnieuw uit.
   * Alleen in beeld bij projecten waar de instelling aanstaat.
   */
  grainSize?: LayerGrainSize | null;
};

export type Boring = {
  id?: string;
  projectId?: string;
  recordingId?: string | null;  // null = manually created, no audio recording
  name: string;
  type?: string | null;         // BORING_TYPE code, bv. "BO" of "IB" (→ TI MpTypeCode). Lees via normalizeBoringType().
  // Sleufmaten — alleen bij type 'Inspectiegat met boring' (→ TI MpTrialPitLength/MpTrialPitWidth).
  trialPitLengthM?: number | null;
  trialPitWidthM?: number | null;
  // Zeefmassa's bij asbestonderzoek (kg). Totaal is leeg zolang de som volstaat; percentages
  // worden afgeleid met massFractions() en nooit opgeslagen.
  massTotalKg?: number | null;
  massGt20Kg?: number | null;
  massLt20Kg?: number | null;
  boormeester?: string | null;
  gwsFound?: boolean | null;    // grondwaterstand aangetroffen? (→ TI MpGWSFoundCode J/N)
  gwsCmMv?: number | null;      // diepte grondwaterstand GWS (cm -mv) → TI MpGwtCM
  x?: number | null;
  y?: number | null;
  z?: number | null;              // ground-level height (maaiveldhoogte) in the project height datum (m, +up). NAP now. Canonical, exported.
  zEllipsoidalRaw?: number | null; // raw ellipsoidal GPS height (m). Provenance only — re-reduce to a datum later; never reported.
  referencePlane?: string | null;  // REFERENCE_PLANES code (MA/WS/WB) — surface depths are measured from. Default MA (maaiveld).
  gpsSource?: string | null;      // which device produced the coordinates: 'device' (internal) | 'external' (Trimble/RTK)
  gpsAccuracy?: number | null;    // horizontal accuracy (m, 68% radius) at the time of the fix
  gpsVerticalAccuracy?: number | null; // vertical accuracy (m) at the time of the fix
  datum?: string | null;          // the boring's DATE (dag), not a vertical datum — legacy field name.
  stopReason?: string | null;   // STOP_CRITERIUM code — reden boring gestopt (MpStopCriterionCode)
  drillingSystem?: string | null; // BOORSYSTEEM code — boormethode van de boring (→ TI LaDrillingSystemTypeCode)
  groundLevelType?: string | null; // MAAIVELDTYPE code — maaiveldtype van de boring (→ TI MpGroundLevelTypeCode)
  opmerking?: string | null;
  afwerking?: Afwerking | null;   // dichten van het boorgat (vulling/casing/bovenafwerking)
  watermonsters?: Watermonster[] | null; // peilbuis-watermonsters (1..n), elk met 1..n flessen
  /** Wanneer het profiel van een ingesproken boring in het veld is goedgekeurd ('klopt'); null = nog te controleren. */
  reviewedAt?: string | null;
  /** Het geplande boorpunt (monsterplan) dat deze boring uitvoert; null = niet gekoppeld. */
  plannedPointId?: string | null;
  layers?: Layer[];
};

/**
 * Watermonster uit een peilbuis. Afgeleid van TerraIndex `Ws` (frmWatersampleDetails) als
 * exportdoel, aangevuld met SIKB-gaten (tijd, zuurstof, debiet, drijf/zaklaag). NEN 6693
 * hoofdstuk 11 dekt alleen de zintuiglijke waarneming; bemonstering zelf staat in SIKB 2002.
 */
export type Watermonster = {
  id?: string;
  name?: string | null;            // monstercode — WsName
  date?: string | null;            // YYYY-MM-DD — WsDate
  time?: string | null;            // HH:MM (SIKB; TI kent geen tijd)
  sampler?: string | null;         // monsternemer — WsSampleTakerCode
  matrix?: string | null;          // WsMatrixCode; default 'grondwater'
  peilbuisId?: string | null;      // link naar de peilbuis
  // Grondwater / voorpompen
  gwsBeforePrepumpCm?: number | null;       // GWS voor voorpompen — WsGwt
  gwsReferencePoint?: string | null;        // ref.vlak GWS — WsGwtReferencePointTypeCode
  prepumpVolumeL?: number | null;           // voorgepompt volume — WsAmountPumped
  gwsAfterPrepumpCm?: number | null;        // GWS na voorpompen — WsGWLafterPrepumping
  gwsBeforeSamplingCm?: number | null;      // GWS voor bemonstering — WsGWLBeforeSampling
  recommendedPrepumpVolumeL?: number | null; // volume te voorpompen — WsRecommendedPrepumpVolume
  flushVolumeL?: number | null;             // spoelvolume — WsTubeFlushVolume
  yield?: string | null;                    // opbrengst goed/matig/slecht — WsYieldCode
  pumpType?: string | null;                 // pomp monstername — WsPumpTypeCode
  flowRate?: number | null;                 // debiet (l/min) — SIKB; TI mist dit
  aerated?: boolean | null;                 // belucht — WsGwlBelowFilter
  // Veldmeting
  ec?: number | null;                       // EC stabiel (µS/cm) — WsEc3
  ph?: number | null;                       // WsPh
  temperature?: number | null;              // °C — TI-waarneming type 12 (eenheid 8)
  oxygen?: number | null;                   // zuurstof (mg/l) — SIKB; TI mist dit
  redox?: number | null;                    // Eh/redox (mV) — WsRedox
  turbidityPrepump?: number | null;         // troebelheid voorpompen (NTU) — WsTurbidityPrePumped
  turbiditySample?: number | null;          // troebelheid monster (NTU) — WsTurbiditySample
  // Zintuiglijk (NEN 6693 h11) — geen actieve geur op het monster (11.4)
  colourShade?: string | null;              // kleursterkte — WsColourShadeCode
  colourMain?: string | null;               // hoofdkleur — WsColourMainCode
  colourSub?: string | null;                // bijkleur — WsColourSubCode
  clarity?: string | null;                  // transparantie helder/half/ondoorzichtig — WsClarityCode
  specialConstituents?: string | null;      // bijzondere bestanddelen (vrije tekst)
  // Drijf/zaklaag (NEN 5744)
  floatingLayer?: boolean | null;
  floatingLayerThicknessMm?: number | null;
  sinkingLayer?: boolean | null;
  sinkingLayerThicknessMm?: number | null;
  // Overig
  transportDate?: string | null;            // datum monstervervoer — WsSampleSendDate
  remark?: string | null;                   // WsRemark
  bottles?: WaterBottle[];
};

/** Fles onder een watermonster (SIKB-blok Flessen). Eén monster → meerdere flessen. */
export type WaterBottle = {
  id?: string;
  name?: string | null;            // volgnummer/naam
  barcode?: string | null;         // verplicht (herleidbaarheid, protocol 2001 eis 19)
  filtered?: boolean | null;       // gefiltreerd ja/nee
  filterType?: string | null;      // verplicht als gefiltreerd
  bottleType?: string | null;      // flestype (korte lijst)
  analysisPackage?: string | null; // analysepakket
  preservation?: string | null;    // conservering
  remark?: string | null;
};

/** Afwerking van het boorgat (hangt aan het meetpunt). Beide NEN-standaarden identiek. */
export type Afwerking = {
  fillings?: AfwerkingFilling[];             // blok 1 — vulling boorgat (verplicht ≥1 bij 6693)
  achtergelatenObjecten?: AchtergelatenObject[]; // blok 2 — achtergelaten in de grond (optioneel)
  casings?: AfwerkingCasing[];              // legacy — oude 'casing'-vorm; alleen nog gelezen voor back-compat
  topCompletions?: AfwerkingTop[];          // blok 3 — bovenafwerking bij een peilbuis (optioneel)
};
export type AfwerkingFilling = { van?: number | null; tot?: number | null; materiaal?: string | null; opmerking?: string | null };
/** Object dat in de grond is achtergelaten (codelijst C). `aard` is het kern-onderscheid;
 *  `opmerking` is verplicht bij aard='achtergebleven' (dan vertelt het pas wat er ligt). */
export type AchtergelatenObject = {
  aard?: 'geplaatst' | 'achtergebleven' | null;
  type?: string | null;          // codelijst C (CAS/FIL/GER/DIV/SEN/BRO/OVE)
  van?: number | null;
  tot?: number | null;
  materiaal?: string | null;     // optioneel
  opmerking?: string | null;     // verplicht bij aard='achtergebleven'
  verwijderdOp?: string | null;  // optioneel — datum waarop het object later is verwijderd
};
export type AfwerkingCasing = { van?: number | null; tot?: number | null; leftInGround?: string | null; opmerking?: string | null };
/** Bovenafwerking. `type` is een samengestelde code die het materiaal al bevat (zie
 *  nen6693.BOVENAFWERKING). `heightCm` = hoogte boven maaiveld. De overige velden zijn legacy
 *  (oude losse-materiaal-vorm) en worden alleen nog gelezen voor achterwaartse compatibiliteit. */
export type AfwerkingTop = { type?: string | null; heightCm?: number | null; materiaal?: string | null; afsluitbaar?: string | null; vloeistofdicht?: string | null; afmeting?: string | null; merk?: string | null };

export type Monster = {
  id?: string;
  boringId?: string | null;
  projectId?: string;
  name?: string | null;
  identificatie?: string | null;
  type?: string | null;         // SIKB code e.g. "Jar"
  upperDepth?: number | null;   // cm
  lowerDepth?: number | null;   // cm
  datum?: string | null;
  geroerd?: boolean | null;     // JrStirred — geroerd (default Ja/true)
  packingType?: string | null;  // JrPackingTypeCode — type verpakking
  opmerking?: string | null;
  /** Aanmaakmoment; de veldapp ordent de scanlijst hierop (oud → nieuw). */
  createdAt?: string | null;
};

// Stub types — fields TBD when implemented
export type Peilbuis = {
  id?: string;
  projectId?: string;
  boringId?: string | null;     // optional: installed in an existing boring
  name?: string | null;
  filterTopCm?: number | null;
  filterBottomCm?: number | null;
  diameter?: number | null;     // buitendiameter (mm) — TI FtDiameter
  material?: string | null;     // PEILBUIS_MATERIAAL code e.g. "1" for PVC
  flowType?: string | null;     // PEILBUIS_DOORLOOP code e.g. "S" for Snel
  remark?: string | null;
  installedAt?: string | null;
  // Uitgebreide velden (TerraIndex TI_peilbuis, prefix Ft)
  innerDiameterMm?: number | null;   // binnendiameter (mm) — FtInnerDiameter
  filterSieveUsed?: boolean | null;  // filterkous — FtFilterSieveUsed
  unwatered?: boolean | null;        // leeggezogen — FtUnwatered
  rinsed?: boolean | null;           // gespoeld — FtRinsed
  ph?: number | null;                // pH bij plaatsing — FtPH
  ec?: number | null;                // EGV bij plaatsing (µS/cm) — FtEC
  temperature?: number | null;       // temperatuur bij plaatsing (°C)
  pumpedL?: number | null;           // afgepompt bij plaatsing (l)
  waterUsageL?: number | null;       // werkwater (l) — FtWaterUsage
  ecWorkWater?: number | null;       // EGV werkwater (µS/cm) — FtECWorkWater
  referencePointType?: string | null;
  topM?: number | null;
  refTopCode?: string | null;
};

/**
 * De peilbuisvelden uit het blok 'Bij plaatsing': wat er tijdens hét plaatsen van díe buis is
 * gemeten. Bij het DUPLICEREN van een boring gaan ze niet mee — pH, EC en temperatuur van het
 * ene boorgat zeggen niets over het volgende, en overnemen zou verzonnen meetwaarden opleveren
 * die later niet meer van echte te onderscheiden zijn.
 *
 * De constructie van de buis (filterdiepte, materiaal, diameter, filterkous) gaat wél mee —
 * dat is een ontwerpkeuze die je juist herhaalt.
 *
 * Staat hier omdat backend én veldapp allebei kunnen dupliceren; twee lijstjes die uit elkaar
 * lopen zou betekenen dat het van je apparaat afhangt wat er in je rapport belandt.
 */
export const PEILBUIS_PLACEMENT_FIELDS = [
  'unwatered', 'rinsed', 'flowType',
  'ph', 'ec', 'ecWorkWater', 'temperature', 'waterUsageL', 'pumpedL',
  'remark',
] as const;

export type Recording = {
  id: string;
  projectId: string;
  filename: string;
  status: 'uploaded' | 'transcribing' | 'transcribed' | 'processing' | 'processed' | 'error';
  transcriptionText?: string | null;
  resultData?: ResultData | null;
  soilStandard?: string | null; // classificatiestandaard (nen5104/nen6693); null = projectdefault
  createdAt?: string | null;
  updatedAt?: string | null;
};

export type Project = {
  id: string;
  name?: string | null;
  code?: string | null;
  status?: string | null;
  borings?: Boring[];
};

/** The raw AI-extracted result_data JSON shape stored per recording. */
export type ResultData = {
  Meetpunten?: JsonMeetpunt[];
  Lagen?: JsonLaag[];
  Monsters?: JsonMonster[];
  Project?: JsonProject;
};

// ─── JSON envelope types (the { waarde: ... } format used in result_data) ────

export type Waarde<T> = { waarde: T };

export type JsonMeetpunt = {
  guid?: string;
  name?: Waarde<string> | string;
  identificatie?: Waarde<string> | string;
  typeOnderzoek?: Waarde<string>;
  boormeester?: Waarde<string>;
  boorsysteem?: Waarde<string>;
  maaiveldtype?: Waarde<string>;
  gws_found?: Waarde<boolean>;
  gws_cm_mv?: Waarde<number>;
  datum?: Waarde<string>;
  coordinaten?: Waarde<string>;
  opmerking?: Waarde<string>;
};

export type JsonLaag = {
  guid?: string;
  meetpuntGuid?: string;
  meetpuntName?: Waarde<string> | string;
  upperDepth_cm?: Waarde<number> | number;
  lowerDepth_cm?: Waarde<number> | number;
  grondsoort?: (Waarde<string> & { sikb_id?: string }) | null;
  mediaan?: Waarde<string> | null;
  kleur?: JsonKleur | null;
  bzb?: JsonBzb[];
  bijmengingen?: JsonBijmenging[];
  opmerking?: Waarde<string>;
  bodemkenmerk?: Waarde<string>;
  bodemkenmerkGradatie?: Waarde<string>;
};

export type JsonKleur = {
  ti_color_main?: string | null;
  ti_color_shade?: string | null;
  ti_color_sub?: string | null;
};

export type JsonBzb = {
  ti_bzb_type?: string | null;    // 2-letter SIKB code e.g. "HO"
  type_naam?: string | null;      // human label
  ti_bzb_gradation?: string | null;
  ti_bzb_origin?: string | null;
};

export type JsonBijmenging = {
  toevoeging?: string | null;
  gradatie?: string | null;
};

export type JsonMonster = {
  guid?: string;
  meetpuntGuid?: string;
  meetpuntName?: Waarde<string> | string;
  gekoppeld_meetpunt?: Waarde<string> | string;
  naam?: Waarde<string>;
  identificatie?: Waarde<string>;
  typeMonster?: Waarde<string>;
  upperDepth_cm?: Waarde<number>;
  lowerDepth_cm?: Waarde<number>;
  diepteVan?: Waarde<number>;
  diepteTot?: Waarde<number>;
  datum?: Waarde<string>;
  omschrijving?: Waarde<string>;
};

export type JsonProject = {
  guid?: string;
  name?: Waarde<string>;
  projectCode?: Waarde<string>;
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Extract the string value from either a plain string or { waarde: string } */
export function unwrapName(val: Waarde<string> | string | undefined | null): string | null {
  if (!val) return null;
  if (typeof val === 'string') return val.trim() || null;
  return (val.waarde ?? '').trim() || null;
}

/** Extract numeric value from either a plain number or { waarde: number } */
export function unwrapNumber(val: Waarde<number> | number | undefined | null): number | null {
  if (val === null || val === undefined) return null;
  if (typeof val === 'number') return val;
  return val.waarde ?? null;
}

/**
 * Convert a JsonLaag (raw result_data format) to a normalized flat Layer.
 * Safe to call regardless of whether values use { waarde } or plain format.
 */
export function jsonLaagToLayer(j: JsonLaag): Layer {
  return {
    upperDepth: unwrapNumber(j.upperDepth_cm) ?? 0,
    lowerDepth: unwrapNumber(j.lowerDepth_cm) ?? 0,
    soilType: j.grondsoort?.sikb_id ?? unwrapName(j.grondsoort as Waarde<string>) ?? null,
    mediaan: unwrapName(j.mediaan) ?? null,
    kleurMain: j.kleur?.ti_color_main ?? null,
    kleurShade: j.kleur?.ti_color_shade ?? null,
    kleurSub: j.kleur?.ti_color_sub ?? null,
    toevoeging1: j.bijmengingen?.[0]?.toevoeging ?? null,
    gradatie1: j.bijmengingen?.[0]?.gradatie ?? null,
    toevoeging2: j.bijmengingen?.[1]?.toevoeging ?? null,
    gradatie2: j.bijmengingen?.[1]?.gradatie ?? null,
    toevoeging3: j.bijmengingen?.[2]?.toevoeging ?? null,
    gradatie3: j.bijmengingen?.[2]?.gradatie ?? null,
    bzb: j.bzb?.map(b => ({
      code: b.ti_bzb_type ?? null,
      type: b.type_naam ?? null,
      gradation: b.ti_bzb_gradation ?? null,
      origin: b.ti_bzb_origin ?? null,
    })) ?? [],
    opmerking: unwrapName(j.opmerking) ?? null,
  };
}

/**
 * Convert a normalized flat Layer back to a JsonLaag for storing in result_data.
 * Always writes the { waarde } envelope so the backend and backoffice can read it.
 */
export function layerToJsonLaag(l: Layer, boreholeName: string): JsonLaag {
  return {
    guid: l.id,
    meetpuntName: { waarde: boreholeName },
    upperDepth_cm: { waarde: l.upperDepth },
    lowerDepth_cm: { waarde: l.lowerDepth },
    grondsoort: l.soilType ? { waarde: l.soilType, sikb_id: l.soilType } : null,
    mediaan: l.mediaan ? { waarde: l.mediaan } : null,
    kleur: l.kleurMain
      ? { ti_color_main: l.kleurMain, ti_color_shade: l.kleurShade ?? null, ti_color_sub: l.kleurSub ?? null }
      : null,
    bijmengingen: [
      ...(l.toevoeging1 || l.gradatie1 ? [{ toevoeging: l.toevoeging1 ?? undefined, gradatie: l.gradatie1 ?? undefined }] : []),
      ...(l.toevoeging2 || l.gradatie2 ? [{ toevoeging: l.toevoeging2 ?? undefined, gradatie: l.gradatie2 ?? undefined }] : []),
      ...(l.toevoeging3 || l.gradatie3 ? [{ toevoeging: l.toevoeging3 ?? undefined, gradatie: l.gradatie3 ?? undefined }] : []),
    ],
    bzb: l.bzb?.map(b => ({
      ti_bzb_type: b.code ?? null,
      type_naam: b.type ?? null,
      ti_bzb_gradation: b.gradation ?? null,
      ti_bzb_origin: b.origin ?? null,
    })) ?? [],
    opmerking: l.opmerking ? { waarde: l.opmerking } : undefined,
  };
}
